/**
 * Environment Configuration
 *
 * Zod-validated import.meta.env with defaults. Fails fast at module load
 * with a readable message when the environment is misconfigured.
 * VITE_API_BASE_URL is the single canonical WordPress REST API base URL.
 */

import { z } from 'zod';
import { SEO_DEFAULTS } from '../../shared/seo/defaults';

/** 'true'/'false' string flag -> boolean, with default */
const boolFlag = (def: boolean) =>
  z.enum(['true', 'false']).default(def ? 'true' : 'false').transform((v) => v === 'true');

/** Optional public URL: empty string (unset) or an http(s) URL. */
const optionalUrl = z
  .string()
  .default('')
  .refine((v) => v === '' || /^https?:\/\/.+/.test(v), { message: 'must be empty or a valid http(s) URL' });

export const envSchema = z.looseObject({
  // WordPress / CMS — CANONICAL API base URL (decision D4).
  // Defaults to freshSTART's WordPress on the KGP API host. Empty string is
  // still allowed and means "no WordPress" — pages render coded content only.
  VITE_API_BASE_URL: z
    .string()
    .default('https://api.kgprodesign.com/freshstart/wp-json')
    .refine((v) => v === '' || /^https?:\/\/.+/.test(v), {
      message: 'must be empty (no WordPress) or a valid http(s) URL',
    }),
  // REST base of the site's WordPress grouping taxonomy (wordpress/freshstart.json
  // `taxonomy.rest_base`) — used to filter photos/testimonials by term.
  VITE_WP_GROUPING_TAXONOMY: z
    .string()
    .regex(/^[a-z][a-z0-9_-]*$/, 'must be a WordPress REST base (lowercase slug)')
    .default('topics'),
  // Public social profile URLs (Footer SocialLinks). Unset = not shown.
  VITE_SOCIAL_FACEBOOK: optionalUrl,
  VITE_SOCIAL_INSTAGRAM: optionalUrl,
  VITE_SOCIAL_X: optionalUrl,
  VITE_SOCIAL_LINKEDIN: optionalUrl,
  VITE_SOCIAL_YOUTUBE: optionalUrl,
  // Givebutter campaign URL (PRD 9.5). A givebutter.com/<code> link is embedded on /store and
  // every Donate CTA leads there; any other URL is opened in a new tab instead.
  // Unset = Donate CTAs fall back to the coded /store Donate page.
  VITE_GIVEBUTTER_URL: optionalUrl,
  // Public contact details shown on Get Help / Privacy / Contact (build-time;
  // PRD v0.4 has no settings DB). Phone is PENDING the board (OQ 11/14) — unset =
  // the phone line is hidden and copy points to email instead.
  VITE_CONTACT_EMAIL: z.string().default('freshstartimpact1@gmail.com'),
  VITE_CONTACT_PHONE: z.string().default(''),
  // SEO (PRP-12). Canonical origin — PENDING PB5: the real domain does not exist
  // yet; set VITE_SITE_URL in netlify.toml once it is registered.
  VITE_SITE_URL: z
    .string()
    .default(SEO_DEFAULTS.siteUrl)
    .refine((v) => /^https?:\/\/[^/]+$/.test(v), { message: 'must be an http(s) origin with no trailing slash' }),
  VITE_SITE_DESCRIPTION: z.string().default(SEO_DEFAULTS.description),
  /** Default share image: a site-relative path or an absolute URL. */
  VITE_DEFAULT_OG_IMAGE: z.string().default(SEO_DEFAULTS.ogImage),
  // Optional GA4 measurement id (G-XXXX). Unset = zero analytics code runs.
  VITE_GA4_ID: z
    .string()
    .default('')
    .refine((v) => v === '' || /^G-[A-Z0-9]+$/.test(v), { message: 'must be empty or a GA4 id like G-ABC123' }),
  // Show the street address in structured data (PRD OQ 16 — default off).
  VITE_PUBLIC_ADDRESS: boolFlag(false),
  VITE_CLIENT_THEME: z.string().default('default'),
  VITE_BLOG_NAME: z.string().default('Blog'),
  VITE_SITE_NAME: z.string().default('freshSTART CDC'),
  VITE_MENU_CONFIG: z.string().default(''),
  VITE_HERO_HEADLINE: z.string().optional(),
  // Feature flags: dark mode is the only BUILD-level flag (ThemeProvider);
  // the others are runtime settings (features.* via /api/config/public).
  VITE_ENABLE_DARK_MODE: boolFlag(true),
});

export type Env = z.infer<typeof envSchema>;

const parsed = envSchema.safeParse(import.meta.env);
if (!parsed.success) {
  // Fail fast with a readable message
  throw new Error(`[config/env] Invalid environment configuration:\n${z.prettifyError(parsed.error)}`);
}
export const env: Env = parsed.data;
